/**
 * Timeline View - Gantt-Style Timeline Chart
 *
 * Displays tasks in a Gantt chart format showing:
 * - Task bars positioned by start/end week
 * - Phase swimlanes (Foundation, Standardization, Automation)
 * - Dependency arrows (curved SVG lines)
 * - Critical path highlighting (gold border)
 * - Week columns with phase quarters
 *
 * Design: Clean Gantt chart with interactive task bars
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SCHEDULE_UNAVAILABLE_LABEL,
  formatCurrency,
  formatEffortWeeks,
  getStatusLabel,
  inclusiveWeekSpan,
  scheduleKindOf,
} from './actionPlanValueFormat';
import { anyFilterActive, matchesFilters } from './actionPlanMatrixPlacement';
import type { MatrixFilters } from './actionPlanMatrixPlacement';
import type { Task, Phase, Dependency, TaskStatus,
  TaskStatusPersisted } from '../types/actionPlan';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface TimelineViewProps {
  tasks: Task[];
  phases: Phase[];
  dependencies: Dependency[];
  onTaskClick: (taskId: string) => void;
  _onTaskStatusChange: (taskId: string, status: TaskStatus) => void;
  /**
   * The currently selected task's canonical Action Plan id, or null.
   *
   * Read from view/URL state by `ActionPlanPanel`, so a task stays selected when the
   * reader moves between views (S5 production). Selection was previously written to
   * component state and never read back, which is why it could not survive a switch.
   */
  selectedTaskId?: string | null;
  /** The plan-wide filter object (S4f-B) — consumed identically to the Matrix. */
  filters: MatrixFilters;
  onClearFilters: () => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Local `formatCurrency` removed: it returned '$0' for an absent value, the
// same defect in a sixth place. One shared implementation (P0-4).

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'critical': return '#dc2626'; // red
    case 'high': return '#f59e0b'; // orange
    case 'medium': return '#3b82f6'; // blue
    case 'low': return '#9ca3af'; // gray
    default: return '#9ca3af';
  }
};

/**
 * `blocked` and `skipped` are persisted states, not settable ones. They get
 * their own treatment rather than being folded into not-started — see the note
 * on getStatusIcon in ListView.
 */
const getStatusOpacity = (status: TaskStatusPersisted): number => {
  switch (status) {
    case 'completed': return 1.0;
    case 'in_progress': return 0.8;
    case 'not_started': return 0.5;
    case 'blocked': return 0.4;
    case 'skipped': return 0.3;
    default: return 0.5;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const TimelineView: React.FC<TimelineViewProps> = ({
  tasks,
  phases,
  onTaskClick,
  selectedTaskId = null,
  filters,
  onClearFilters,
}) => {
  /*
   * THREE SCHEDULE POPULATIONS, MUTUALLY EXCLUSIVE (2A follow-up).
   *
   * Every task used to get a bar, positioned from `start_week`/`end_week` alone. 2A made
   * effort nullable and fixed the tooltip, which left the actual defect untouched: a task
   * nobody had estimated still drew a bar. Because the generator defines
   * `end_week = start_week + effort_weeks - 1`, an unestimated task's stored range collapses
   * to a zero- or negative-width sliver at whatever week the document recorded — a placed,
   * zero-duration piece of work that nobody scheduled.
   *
   *   scheduled    effort > 0   -> a bar spanning inclusive weeks
   *   milestone    effort === 0 -> an explicit marker. `end_week` is `start_week - 1` for
   *                these, so ANY width formula renders them invisible or negative.
   *   unavailable  effort null  -> the "Schedule unavailable" band. NOT a milestone: a
   *                milestone asserts a date, and these have none.
   *
   * Partitioned by `scheduleKindOf`, so the three sets are exhaustive and disjoint by
   * construction and every task appears exactly once. Tested.
   */
  /*
   * THE PLAN-WIDE FILTERS, CONSUMED IDENTICALLY TO THE MATRIX (S4f-B).
   * Visibility only: the schedule partition and the canvas extent derive from the
   * FULL set, so filtering never re-times the chart or moves a task between the
   * three schedule populations — it only decides which of them render.
   */
  const filtersActive = anyFilterActive(filters);
  const visibleTotal = useMemo(
    () => tasks.filter((t) => matchesFilters(t, filters)).length,
    [tasks, filters],
  );

  const { scheduledTasks, milestoneTasks, unschedulableTasks } = useMemo(() => {
    const scheduled: Task[] = [];
    const milestone: Task[] = [];
    const unschedulable: Task[] = [];
    for (const t of tasks) {
      const kind = scheduleKindOf(t.effort_weeks);
      if (kind === 'scheduled') scheduled.push(t);
      else if (kind === 'milestone') milestone.push(t);
      else unschedulable.push(t);
    }
    return {
      scheduledTasks: scheduled,
      milestoneTasks: milestone,
      unschedulableTasks: unschedulable,
    };
  }, [tasks]);

  /*
   * Canvas extent, from what is actually DRAWN.
   *
   * Unschedulable tasks are excluded: their stored `end_week` is content the view no longer
   * honours, so letting it stretch the canvas would size the grid around a task that has no
   * bar on it. `Math.max()` of an empty list is -Infinity, which propagated into every width
   * as NaN, so an empty or wholly-unschedulable plan floors at 0.
   */
  const maxWeek = useMemo(() => {
    const ends = [
      ...scheduledTasks.map((t) => t.end_week),
      ...milestoneTasks.map((t) => t.start_week),
      ...phases.map((p) => p.end_week),
    ].filter((w) => Number.isFinite(w));
    return ends.length > 0 ? Math.max(0, ...ends) : 0;
  }, [scheduledTasks, milestoneTasks, phases]);

  const weekColumns = useMemo(() => {
    const columns = [];
    for (let week = 0; week <= maxWeek; week++) {
      columns.push(week);
    }
    return columns;
  }, [maxWeek]);

  // Group tasks by phase
  const tasksByPhase = useMemo(() => {
    const grouped = new Map<string, Task[]>();
    phases.forEach(phase => {
      // Swimlanes carry drawable tasks only. Unschedulable ones live in their own band,
      // so including them here would put them back on the grid by another route.
      grouped.set(
        phase.id,
        [...scheduledTasks, ...milestoneTasks].filter(
          (t) => t.phase_id === phase.id && matchesFilters(t, filters),
        ),
      );
    });
    return grouped;
    // `filters` is a dependency for real: without it a filter change rendered
    // the previous filter's swimlanes.
  }, [scheduledTasks, milestoneTasks, phases, filters]);

  /*
   * INCLUSIVE WEEK BOUNDARIES (2A follow-up).
   *
   * `weekColumns` renders 0..maxWeek, which is `maxWeek + 1` columns, while every canvas width
   * was `maxWeek * WEEK_WIDTH` — one column narrower than the grid it had just drawn. And task
   * width was `(end_week - start_week) * WEEK_WIDTH`, but the generator's
   * `end_week = start_week + effort_weeks - 1` means BOTH endpoints are occupied: a one-week
   * task came out zero-width and every longer task was drawn exactly one week short.
   *
   * `CANVAS_WIDTH` and `inclusiveWeekSpan` fix both from the same definition, so the header,
   * the quarter bar, the swimlane grid and the task bars cannot drift apart again.
   */
  /*
   * FIT PLAN / ZOOM (S4f-B). Three explicit modes: `fit` sizes weeks so the whole
   * plan spans the visible scroller (measured, floored at 8px so labels survive);
   * `compact` and `comfortable` are fixed widths. State is view-local — zoom is
   * presentation, not plan state.
   */
  const TOTAL_COLUMNS = maxWeek + 1;
  const [zoomMode, setZoomMode] = useState<'fit' | 'compact' | 'comfortable'>('comfortable');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const [fitWidth, setFitWidth] = useState<number>(12);
  const measureFit = useCallback(() => {
    const el = scrollRef.current;
    if (!el || TOTAL_COLUMNS === 0) return;
    const usable = Math.max(0, el.clientWidth - 200);
    setFitWidth(Math.max(8, Math.floor(usable / TOTAL_COLUMNS)));
  }, [TOTAL_COLUMNS]);
  const WEEK_WIDTH = zoomMode === 'fit' ? fitWidth : zoomMode === 'compact' ? 24 : 40;
  const CANVAS_WIDTH = TOTAL_COLUMNS * WEEK_WIDTH;

  /*
   * SYNCHRONIZED TOP + BOTTOM HORIZONTAL CONTROLS (S4f-B): a proxy scrollbar above
   * the chart mirrors the main scroller both ways, so weeks 44-49 of a 49-week plan
   * are reachable without first scrolling through every row. The guard prevents a
   * feedback loop between the two onScroll handlers.
   */
  /*
   * No pending-flag guard: the equality check alone terminates the echo. A
   * mirrored assignment fires (at most) one scroll event on the other element,
   * whose handler finds the positions already equal and returns — and a flag
   * would LEAK in any environment where the programmatic assignment fires no
   * event at all, swallowing the user's next real scroll.
   */
  const syncFrom = (source: 'top' | 'main') => () => {
    const from = source === 'top' ? topScrollRef.current : scrollRef.current;
    const to = source === 'top' ? scrollRef.current : topScrollRef.current;
    if (!from || !to || from.scrollLeft === to.scrollLeft) return;
    to.scrollLeft = from.scrollLeft;
  };

  useEffect(() => {
    measureFit();
    window.addEventListener('resize', measureFit);
    return () => window.removeEventListener('resize', measureFit);
  }, [measureFit]);
  /** A zero-duration event needs a visible marker width; a span would be 0 or negative. */
  const MILESTONE_WIDTH = 16;
  const TASK_HEIGHT = 32; // px per task bar
  const TASK_SPACING = 8; // px between tasks
  const PHASE_HEADER_HEIGHT = 40; // px
  const TIMELINE_HEADER_HEIGHT = 60; // px

  return (
    <div className="timeline-view">
      {/* S4f-B: zoom controls + the same visible result count every view shows. */}
      <div className="ap-tl-controls" role="group" aria-label="Timeline zoom and filters">
        <button
          type="button"
          aria-pressed={zoomMode === 'fit'}
          onClick={() => { setZoomMode('fit'); measureFit(); }}
        >
          Fit plan
        </button>
        <button
          type="button"
          aria-pressed={zoomMode === 'compact'}
          onClick={() => setZoomMode('compact')}
        >
          Compact
        </button>
        <button
          type="button"
          aria-pressed={zoomMode === 'comfortable'}
          onClick={() => setZoomMode('comfortable')}
        >
          Comfortable
        </button>
        <span className="ap-view-count" data-testid="ap-timeline-result-count">
          Showing {visibleTotal} of {tasks.length} tasks
        </span>
      </div>

      {filtersActive && visibleTotal === 0 && tasks.length > 0 && (
        <div className="ap-matrix-zero" role="status">
          <p>No tasks match the current filters.</p>
          <button type="button" className="ap-matrix-toolbar__clear" onClick={onClearFilters}>
            Clear filters
          </button>
        </div>
      )}

      {/* The TOP horizontal control, synchronized with the chart scroller below —
          weeks 44-49 of a 49-week plan are reachable without traversing every row. */}
      <div
        className="ap-tl-topscroll"
        ref={topScrollRef}
        onScroll={syncFrom('top')}
        data-testid="ap-timeline-topscroll"
        aria-hidden="true"
      >
        <div style={{ width: `${200 + CANVAS_WIDTH}px`, height: '1px' }} />
      </div>

      <div
        className="ap-tl-scroll"
        ref={scrollRef}
        onScroll={syncFrom('main')}
        data-testid="ap-timeline-scroll"
        role="region"
        aria-label="Timeline chart — scrolls horizontally; the matching control is above the chart"
      >
      {/* Timeline Header (Week columns) — sticky inside the chart scroller. */}
      <div className="timeline-header ap-tl-sticky" style={{ height: `${TIMELINE_HEADER_HEIGHT}px` }}>
        <div className="timeline-header-row">
          <div className="phase-label-column" style={{ width: '200px' }}>
            <span className="header-label">Phase / Task</span>
          </div>
          <div className="week-columns" style={{ width: `${CANVAS_WIDTH}px` }}>
            {weekColumns.map(week => (
              <div
                key={week}
                className="week-column"
                style={{ width: `${WEEK_WIDTH}px` }}
              >
                <span className="week-label">W{week}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quarter markers */}
        <div className="quarter-markers">
          <div className="phase-label-column" style={{ width: '200px' }}></div>
          <div className="quarter-columns" style={{ width: `${CANVAS_WIDTH}px` }}>
            {phases.map(phase => (
              <div
                key={phase.id}
                className="quarter-marker"
                style={{
                  left: `${phase.start_week * WEEK_WIDTH}px`,
                  width: `${inclusiveWeekSpan(phase.start_week, phase.end_week) * WEEK_WIDTH}px`
                }}
              >
                <span className="quarter-label">{phase.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline Body (Phase swimlanes with tasks) */}
      <div className="timeline-body">
        {phases.map(phase => {
          const phaseTasks = tasksByPhase.get(phase.id) || [];
          const phaseHeight = PHASE_HEADER_HEIGHT + (phaseTasks.length * (TASK_HEIGHT + TASK_SPACING)) + 20;

          return (
            <div
              key={phase.id}
              className="phase-swimlane"
              style={{ minHeight: `${phaseHeight}px` }}
            >
              {/* Phase Label */}
              <div className="phase-label" style={{ width: '200px' }}>
                <h4>{phase.name}</h4>
                <span className="phase-weeks">
                  W{phase.start_week} - W{phase.end_week}
                </span>
              </div>

              {/* Phase Timeline Area */}
              <div
                className="phase-timeline"
                style={{ width: `${CANVAS_WIDTH}px` }}
              >
                {/* Week grid */}
                <div className="week-grid">
                  {weekColumns.map(week => (
                    <div
                      key={week}
                      className="week-grid-line"
                      style={{
                        left: `${week * WEEK_WIDTH}px`,
                        width: `${WEEK_WIDTH}px`
                      }}
                    ></div>
                  ))}
                </div>

                {/* Task bars */}
                <div className="task-bars">
                  {phaseTasks.map((task, index) => {
                    const taskLeft = task.start_week * WEEK_WIDTH;
                    // Inclusive: a one-week task occupies one column.
                    const taskWidth =
                      inclusiveWeekSpan(task.start_week, task.end_week) * WEEK_WIDTH;
                    const isMilestone = scheduleKindOf(task.effort_weeks) === 'milestone';
                    const taskTop = index * (TASK_HEIGHT + TASK_SPACING);

                    return (
                      <button
                        type="button"
                        key={task.id}
                        data-testid={isMilestone ? `milestone-${task.id}` : `bar-${task.id}`}
                        data-schedule-kind={isMilestone ? 'milestone' : 'scheduled'}
                        data-ap-task-button={task.id}
                        aria-label={`${task.title} — ${getStatusLabel(task.status)}, ${
                          isMilestone
                            ? `milestone at week ${task.start_week}`
                            : `weeks ${task.start_week} to ${task.end_week}`
                        }${task.is_critical_path ? ', on the critical path' : ''}`}
                        aria-current={selectedTaskId === task.id ? 'true' : undefined}
                        className={`task-bar ${isMilestone ? 'task-milestone' : ''} ${task.is_critical_path ? 'critical-path' : ''} status-${task.status}${selectedTaskId === task.id ? ' is-selected' : ''}`}
                        style={{
                          left: `${taskLeft}px`,
                          /*
                           * A milestone gets a FIXED marker width, not a computed span. Its
                           * `end_week` is `start_week - 1`, so every width formula — the old
                           * exclusive one and the new inclusive one alike — yields 0 or less,
                           * and a zero-effort task would simply not be visible.
                           */
                          width: isMilestone ? `${MILESTONE_WIDTH}px` : `${taskWidth}px`,
                          top: `${taskTop}px`,
                          height: `${TASK_HEIGHT}px`,
                          backgroundColor: getPriorityColor(task.priority),
                          opacity: getStatusOpacity(task.status)
                        }}
                        onClick={() => onTaskClick(task.id)}
                        title={`${task.title} (${isMilestone ? 'milestone, no duration' : formatEffortWeeks(task.effort_weeks)}, ${formatCurrency(task.value_usd?.value ?? null)})`}
                      >
                        {isMilestone && (
                          <span className="milestone-marker" aria-hidden="true">
                            ◆
                          </span>
                        )}
                        {/* Critical-path marker OUTSIDE the clipped content flow
                            (S4h): at Fit zoom a bar can floor at 8px wide, and a
                            star inside `.task-bar-content` clipped to nothing.
                            Anchored at the bar's left corner with its own size,
                            it survives EVERY zoom — and the accessible name
                            carries "on the critical path" regardless. */}
                        {task.is_critical_path && (
                          <span className="critical-star" aria-hidden="true">⭐</span>
                        )}
                        <div className="task-bar-content">
                          <span className="task-bar-title">{task.title}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      </div>

      {/* Legend */}
      <div className="timeline-legend">
        <h4>Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#dc2626' }}></div>
            <span>Critical Priority</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#f59e0b' }}></div>
            <span>High Priority</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#3b82f6' }}></div>
            <span>Medium Priority</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#9ca3af' }}></div>
            <span>Low Priority</span>
          </div>
          <div className="legend-item">
            <span className="legend-symbol">⭐</span>
            <span>Critical Path</span>
          </div>
          {milestoneTasks.length > 0 && (
            <div className="legend-item">
              <span className="legend-symbol">◆</span>
              <span>Milestone (zero duration)</span>
            </div>
          )}
        </div>
      </div>

      {/*
        Tasks that cannot be scheduled, kept VISIBLE rather than dropped (2A follow-up).

        These have no effort estimate, so there is no span to draw and no date to assert.
        Excluding them from the chart is correct; excluding them from the VIEW would silently
        lose work — a reader counting bars would conclude the plan is smaller than it is,
        which is the same failure mode as plotting them at zero, just quieter.

        Deliberately not a milestone: a milestone marker claims "this happens in week N", and
        that is exactly the claim these tasks cannot support.
      */}
      {unschedulableTasks.filter((t) => matchesFilters(t, filters)).length > 0 && (
        <section
          className="timeline-unschedulable"
          aria-label={SCHEDULE_UNAVAILABLE_LABEL}
          data-testid="timeline-unschedulable-band"
        >
          <div className="timeline-unschedulable__header">
            <h4>{SCHEDULE_UNAVAILABLE_LABEL}</h4>
            <span className="timeline-unschedulable__count">
              {filtersActive
                ? String(unschedulableTasks.filter((t) => matchesFilters(t, filters)).length) +
                  ' of ' + String(unschedulableTasks.length)
                : unschedulableTasks.length}
            </span>
          </div>
          <p className="timeline-unschedulable__description">
            These tasks have no effort estimate, so they cannot be placed on the timeline —
            which is different from taking no time. They are still part of the plan.
          </p>
          <ul className="timeline-unschedulable__tasks">
            {unschedulableTasks.filter((t) => matchesFilters(t, filters)).map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  data-testid={`unschedulable-${task.id}`}
                  data-schedule-kind="unavailable"
                  className={`timeline-unschedulable__task status-${task.status}`}
                  onClick={() => onTaskClick(task.id)}
                >
                  {task.is_critical_path && <span className="critical-star">⭐</span>}
                  <span className="timeline-unschedulable__title">{task.title}</span>
                  <span className="timeline-unschedulable__reason">
                    {SCHEDULE_UNAVAILABLE_LABEL}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
